/**
 * Central feature-flag compilation.
 *
 * Single source of truth for which features are live. Flip a value to false to
 * kill-switch a route/feature: gated routes fall through to the catch-all
 * redirect; gated global components simply don't mount. All flags are
 * compile-time constants so disabled code paths are tree-shaken / never mount.
 *
 * NOTE: the api/*.js serverless endpoints are gated separately (cron secrets /
 * admin checks) and are intentionally not represented here.
 */
export const FEATURES = {
  // Public / auth
  landing: true, // /
  login: true, // /login
  register: true, // /register
  inviteLink: true, // /invite/:code
  profileSetup: true, // /profile-setup

  // Core league
  dashboard: true, // /dashboard (league list)
  createLeague: true, // /leagues/create
  joinLeague: true, // /leagues/join
  leagueDashboard: true, // /leagues/:id
  editLeague: true, // /leagues/:id/edit

  // Cricket gameplay
  squadSelection: true, // /leagues/:id/squad
  leaderboard: true, // /leagues/:id/leaderboard
  analytics: true, // /leagues/:id/analytics
  viewTeams: true, // /leagues/:id/teams
  leagueRules: true, // /leagues/:id/rules
  matchSchedule: true, // /leagues/:id/schedule
  scoringSystem: true, // /leagues/:id/scoring
  scheduleUpload: true, // /leagues/:id/schedule/upload

  // User / social
  profile: true, // /profile
  userProfile: true, // /user/:userId

  // Admin
  adminPanel: true, // /admin/*
  cricketPlayerPool: true, // /admin/player-pools
  tennisPlayerPool: true, // /admin/tennis-player-pools
  predictions: true, // /admin/predictions
  powerplay: true, // /admin/powerplay
  hiddenPlayers: true, // /admin/hidden-player
  globalLeaderboard: true, // /admin/global-leaderboard

  // Tennis
  tennisHub: true, // /tennis

  // Global / PWA (mounted in App body)
  whatsNewModal: true,
  installPrompt: true,
  pullToRefresh: true,

  /**
   * Stumpy — the AI League Assistant.
   *
   * PARKED (disabled) as of 2026-06. The feature called Gemini and isn't serving
   * users right now: the Gemini API is disabled and billing is unlinked after a
   * key-abuse incident (see docs/SECURITY.md). All Stumpy code is kept intact as
   * dead code (component: components/LeagueAssistant.tsx, client: services/
   * aiService.ts, server: api/ai-assistant.js) for a future, hardened revival.
   *
   * To re-enable: set this to true AND complete the server-side wiring — a new
   * API-restricted GEMINI_API_KEY in Vercel, re-enable the Generative Language
   * API, relink billing, and set a budget cap.
   */
  stumpyAssistant: false,
} as const;

export type FeatureFlag = keyof typeof FEATURES;

/** Returns whether a feature is enabled. */
export function isEnabled(flag: FeatureFlag): boolean {
  return FEATURES[flag];
}

/**
 * Backward-compatible alias for the original Stumpy flag. Existing consumers
 * (LeagueAssistant.tsx, leagueAssistantGate.test.tsx) keep working unchanged.
 */
export const STUMPY_ENABLED = FEATURES.stumpyAssistant;
